import { DialogComponent } from "@theme/dialog";
import { setLocalStorage, getLocalStorage, prefersReducedMotion } from "@theme/utilities";

const STORAGE_KEY = "pebbletheme:egg-popup";
const KLAVIYO_SUBSCRIBE_URL = "https://a.klaviyo.com/client/subscriptions/";
const KLAVIYO_REVISION = "2025-04-15";
const TOTAL_TAPS = 1;
/**
 * How long the cracked egg is held before it hatches, in ms. Covers the
 * ~440ms crack cross-fade plus a beat of the cracked shell on its own.
 */
const CRACK_HOLD = 800;
/**
 * Fallbacks for the auto-open thresholds, used when the section settings are
 * missing. Seconds on page, and percent of the page scrolled.
 */
const DEFAULT_TRIGGER_DELAY = 9;
const DEFAULT_TRIGGER_SCROLL = 40;

/**
 * Gamified "Crack the Egg" email/SMS capture popup.
 *
 * A floating teaser trigger peeks in from the edge of the viewport on load, and
 * the dialog opens itself once the visitor has either dwelled on the page or
 * scrolled a set depth — whichever comes first — so it interrupts browsing
 * intent rather than arrival. The dialog is 5 steps: tap an egg to hatch it,
 * then submit a Klaviyo form to reveal a discount code. Persistence rules:
 * dismissing via the "No thanks" link hides the trigger for `data-dismiss-days`;
 * a successful subscribe hides it forever; plain closes (Esc/outside/X) leave
 * the trigger available.
 */
class EggPopupComponent extends DialogComponent {
  requiredRefs = ["dialog", "trigger", "egg", "form"];

  /** @type {AbortController | undefined} */
  #autoOpenAbortController;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #autoOpenTimeout;

  /** @type {ResizeObserver | undefined} */
  #pageResizeObserver;

  /** @type {number | undefined} */
  #scrollFrame;

  /**
   * Scroll offset, in px, that opens the dialog. `Infinity` until the page has
   * been measured, and on pages too short to scroll.
   */
  #scrollThreshold = Infinity;

  /** True while the Klaviyo request is in flight. */
  #submitting = false;

  connectedCallback() {
    super.connectedCallback();
    // Covers a tick the browser restored into the form on back/forward.
    this.syncConsent();
    this.#init();
  }

  disconnectedCallback() {
    this.#disarmAutoOpen();
    super.disconnectedCallback();
  }

  #init() {
    // Dismissed or already subscribed: no trigger, and nothing armed.
    if (!Shopify.designMode && getLocalStorage(STORAGE_KEY)) return;

    this.#showTrigger();

    // In the editor the dialog already opens when the merchant selects the
    // section (see DialogComponent); a dwell timer popping it open on top of
    // that would only fight them.
    if (Shopify.designMode) return;

    const delaySeconds = this.#autoOpenSetting("triggerDelay", DEFAULT_TRIGGER_DELAY);
    const scrollPercent = this.#autoOpenSetting("triggerScroll", DEFAULT_TRIGGER_SCROLL);

    // Both thresholds off: the popup only opens when the visitor taps the egg.
    if (delaySeconds <= 0 && scrollPercent <= 0) return;

    this.#autoOpenAbortController = new AbortController();

    if (delaySeconds > 0) {
      this.#autoOpenTimeout = setTimeout(() => this.#autoOpen(), delaySeconds * 1000);
    }

    if (scrollPercent > 0) this.#armScrollThreshold(scrollPercent);
  }

  /**
   * @param {string} key - dataset key holding the setting.
   * @param {number} fallback
   * @returns {number}
   */
  #autoOpenSetting(key, fallback) {
    const value = Number(this.dataset[key]);
    return Number.isFinite(value) ? value : fallback;
  }

  /**
   * Opens the dialog once `percent` of the page has been scrolled.
   *
   * The scroll handler is passive, coalesced to one animation frame, and only
   * compares two numbers: the threshold is (re)measured in a ResizeObserver
   * callback, which runs after layout, so nothing here forces a reflow while
   * the visitor is scrolling. The observer also covers the page growing as
   * lazy content loads and the viewport being resized or rotated.
   *
   * @param {number} percent
   */
  #armScrollThreshold(percent) {
    this.#pageResizeObserver = new ResizeObserver(() => {
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      this.#scrollThreshold = scrollable > 0 ? scrollable * (percent / 100) : Infinity;
      // Covers a restored scroll position that's already past the threshold.
      this.#checkScrollDepth();
    });
    this.#pageResizeObserver.observe(document.documentElement);

    window.addEventListener("scroll", this.#handleScroll, {
      passive: true,
      signal: this.#autoOpenAbortController?.signal,
    });
  }

  #handleScroll = () => {
    if (this.#scrollFrame != null) return;

    this.#scrollFrame = requestAnimationFrame(() => {
      this.#scrollFrame = undefined;
      this.#checkScrollDepth();
    });
  };

  #checkScrollDepth() {
    if (window.scrollY >= this.#scrollThreshold) this.#autoOpen();
  }

  /**
   * Opens the dialog on whichever threshold fired first. Both are torn down
   * before anything else happens, so the other one can never fire as well.
   */
  #autoOpen() {
    this.#clearThresholds();

    const openDialog = document.querySelector("dialog[open]");

    // Never stack on top of the cart, search or menu drawer — wait it out.
    if (openDialog && openDialog !== this.refs.dialog) {
      openDialog.addEventListener("close", () => this.#autoOpen(), {
        once: true,
        signal: this.#autoOpenAbortController?.signal,
      });
      return;
    }

    this.#disarmAutoOpen();
    this.showDialog();
  }

  /**
   * Stops both thresholds. Kept separate from #disarmAutoOpen so #autoOpen can
   * still park a listener on the abort signal while it waits for a drawer.
   */
  #clearThresholds() {
    clearTimeout(this.#autoOpenTimeout);
    this.#autoOpenTimeout = undefined;

    if (this.#scrollFrame != null) cancelAnimationFrame(this.#scrollFrame);
    this.#scrollFrame = undefined;

    this.#pageResizeObserver?.disconnect();
    this.#pageResizeObserver = undefined;

    window.removeEventListener("scroll", this.#handleScroll);
  }

  #disarmAutoOpen() {
    this.#clearThresholds();

    this.#autoOpenAbortController?.abort();
    this.#autoOpenAbortController = undefined;
  }

  #showTrigger() {
    this.refs.trigger.classList.remove("hidden");
    this.refs.trigger.classList.add("egg-popup__trigger--visible");
  }

  openFromTrigger() {
    // A visitor who opened it themselves shouldn't have it opened at them later.
    this.#disarmAutoOpen();

    if (this.getAttribute("step") === "5") this.#reset();
    this.showDialog();
  }

  tapEgg() {
    const taps = Number(this.getAttribute("taps")) || 0;
    if (taps >= TOTAL_TAPS) return;

    this.setAttribute("taps", String(taps + 1));

    const { egg, liveRegion } = this.refs;

    if (!prefersReducedMotion()) {
      egg.classList.remove("is-wobbling");
      void egg.offsetWidth;
      egg.classList.add("is-wobbling");
    }

    // Show the cracked shell briefly, then hatch.
    this.#setStep(2, false);
    if (liveRegion) liveRegion.textContent = "";
    setTimeout(() => this.#setStep(3), CRACK_HOLD);
  }

  /**
   * @param {number} step - parsed from the `on:click="/goToStep/4"` payload.
   */
  goToStep(step) {
    this.#setStep(Number(step));
  }

  dismiss() {
    if (!Shopify.designMode) {
      const days = Number(this.dataset.dismissDays) || 1;
      setLocalStorage(STORAGE_KEY, { state: "dismissed" }, days);
      this.#hideTrigger();
    }
    this.closeDialog();
  }

  /**
   * Keeps the submit button disabled until the email/SMS consent checkbox is
   * ticked, and while a request is in flight.
   */
  syncConsent() {
    const { consentInput, submitButton } = this.refs;
    if (!consentInput || !submitButton) return;

    submitButton.disabled = this.#submitting || !consentInput.checked;
  }

  /**
   * @param {SubmitEvent} event
   */
  async handleSubmit(event) {
    event.preventDefault();

    const { form, formError, phoneError, submitButton, firstNameInput, emailInput, phoneInput } = this.refs;

    formError?.classList.add("hidden");
    phoneError?.classList.add("hidden");

    if (!form.reportValidity()) return;

    const phoneRaw = phoneInput.value.trim();
    let phone = "";

    if (phoneRaw) {
      phone = this.#normalizePhone(phoneRaw);
      if (!phone) {
        this.#showError(phoneError, form.dataset.errorPhone);
        return;
      }
    }

    const companyId = this.dataset.klaviyoCompanyId;
    const listId = this.dataset.klaviyoListId;

    if (!companyId || !listId) {
      console.warn("[egg-popup] Klaviyo public API key or list ID is not configured.");
      this.#showError(formError, form.dataset.errorGeneric);
      return;
    }

    /** @type {Record<string, any>} */
    const subscriptions = { email: { marketing: { consent: "SUBSCRIBED" } } };
    if (phone) subscriptions.sms = { marketing: { consent: "SUBSCRIBED" } };

    /** @type {Record<string, any>} */
    const profileAttributes = {
      email: emailInput.value.trim(),
      first_name: firstNameInput.value.trim(),
      subscriptions,
    };
    if (phone) profileAttributes.phone_number = phone;

    const payload = {
      data: {
        type: "subscription",
        attributes: {
          custom_source: "Crack the Egg popup",
          profile: { data: { type: "profile", attributes: profileAttributes } },
        },
        relationships: { list: { data: { type: "list", id: listId } } },
      },
    };

    this.#submitting = true;
    submitButton.classList.add("btn--loading");
    submitButton.disabled = true;

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 10000);

    try {
      const response = await fetch(`${KLAVIYO_SUBSCRIBE_URL}?company_id=${encodeURIComponent(companyId)}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          revision: KLAVIYO_REVISION,
        },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });

      if (!response.ok) {
        let phoneIssue = false;
        try {
          const body = await response.json();
          phoneIssue = Boolean(
            body?.errors?.some((error) => error?.source?.pointer?.includes("phone_number"))
          );
        } catch (parseError) {
          // Non-JSON error response; fall through to the generic message.
        }

        if (phoneIssue) {
          this.#showError(phoneError, form.dataset.errorPhone);
        } else {
          this.#showError(formError, form.dataset.errorGeneric);
        }
        return;
      }

      this.#handleSuccess(Boolean(phone));
    } catch (fetchError) {
      this.#showError(formError, form.dataset.errorGeneric);
    } finally {
      clearTimeout(timeout);
      this.#submitting = false;
      submitButton.classList.remove("btn--loading");
      this.syncConsent();
    }
  }

  /**
   * @param {boolean} phoneProvided
   */
  #handleSuccess(phoneProvided) {
    const code = (phoneProvided ? this.dataset.codePhone : this.dataset.codeEmail) || "";
    const { codeBox } = this.refs;

    if (codeBox) {
      codeBox.setAttribute("text-to-copy", code);
      const codeText = codeBox.querySelector('[ref="codeText"]');
      if (codeText) codeText.textContent = code;
    }

    if (!Shopify.designMode) {
      setLocalStorage(STORAGE_KEY, { state: "subscribed", code }, null);
      this.#hideTrigger();
    }

    this.#setStep(5);
  }

  /**
   * Normalizes a phone number to E.164. US-biased: bare 10-digit numbers get
   * a +1 prefix; anything already starting with "+" passes through. Returns
   * an empty string when the input can't be normalized.
   *
   * @param {string} raw
   * @returns {string}
   */
  #normalizePhone(raw) {
    if (raw.startsWith("+")) {
      const digits = raw.slice(1).replace(/\D/g, "");
      return digits.length >= 8 && digits.length <= 15 ? `+${digits}` : "";
    }

    const digits = raw.replace(/\D/g, "");
    if (digits.length === 10) return `+1${digits}`;
    if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
    return "";
  }

  /**
   * @param {number} step
   * @param {boolean} [focus] - move focus into the newly revealed panel.
   */
  #setStep(step, focus = true) {
    this.setAttribute("step", String(step));

    if (!focus) return;

    requestAnimationFrame(() => {
      const panel = this.querySelector(`.egg-popup__step[data-step="${step}"]`);
      const target = panel?.querySelector("[data-step-focus]");
      if (target instanceof HTMLElement) target.focus({ preventScroll: true });
    });
  }

  /**
   * @param {Element | undefined} element
   * @param {string | undefined} message
   */
  #showError(element, message) {
    if (!element) return;
    element.textContent = message || "";
    element.classList.remove("hidden");
  }

  #hideTrigger() {
    this.refs.trigger.classList.remove("egg-popup__trigger--visible");
    this.refs.trigger.classList.add("hidden");
  }

  #reset() {
    this.setAttribute("step", "1");
    this.setAttribute("taps", "0");
  }
}

if (!customElements.get("egg-popup-component")) {
  customElements.define("egg-popup-component", EggPopupComponent);
}
